import React, { memo, useCallback } from 'react';
import { Handle, Position, NodeProps } from '@xyflow/react';
import { useStore } from '../../store';
import { ResizeHandle } from './ResizeHandle';


interface CircleNodeData {
  text: string;
  timeEstimate: string;
  parentId: string | null;
  children: string[];
  // Style properties
  fillColor?: string;
  strokeColor?: string;
  textColor?: string;
  fontFamily?: string;
  fontSize?: number;
  opacity?: number;
  textOverflow?: 'hidden' | 'visible';
}

export const CircleNode = memo(({ id, data, selected }: NodeProps) => {
  const nodeData = data as unknown as CircleNodeData;
  const updateNode = useStore((state) => state.updateNode);
  const nodes = useStore((state) => state.nodes);
  const addConnectedNode = useStore((state) => state.addConnectedNode);
  
  const node = nodes[id];

  const handleTextChange = useCallback(
    (e: React.FormEvent<HTMLDivElement>) => {
      // Use innerText to preserve line breaks
      const newText = e.currentTarget.innerText || '';
      updateNode(id, { text: newText });
    },
    [id, updateNode]
  );

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    // Escape to exit edit mode
    if (e.key === 'Escape') {
      e.preventDefault();
      (e.target as HTMLElement).blur();
    }
    // Stop propagation to prevent node deletion while editing
    if (e.key === 'Backspace' || e.key === 'Delete') {
      e.stopPropagation();
    }
    // Allow Enter for new lines (don't prevent default)
  }, []);

  // Apply custom dimensions and styles (circle maintains square shape)
  const style: React.CSSProperties = {};
  if (node?.width) {
    style.width = `${node.width}px`;
    style.height = `${node.width}px`; // Keep it square for circle
  }
  // Apply custom colors from node data
  if (nodeData.fillColor) {
    style.backgroundColor = nodeData.fillColor;
  }
  if (nodeData.strokeColor) {
    style.borderColor = nodeData.strokeColor;
  }
  if (nodeData.textColor) {
    style.color = nodeData.textColor;
  }
  if (nodeData.fontFamily) {
    style.fontFamily = nodeData.fontFamily;
  }
  if (nodeData.fontSize) {
    style.fontSize = `${nodeData.fontSize}px`;
  }
  if (nodeData.opacity !== undefined) {
    style.opacity = nodeData.opacity;
  }

  const handleAddNode = useCallback(
    (direction: 'left' | 'right' | 'top' | 'bottom') => (e: React.MouseEvent) => {
      e.stopPropagation();
      addConnectedNode(id, direction, 'circle');
    },
    [id, addConnectedNode]
  );

  const overflowClass = nodeData.textOverflow === 'visible' ? 'text-overflow-visible' : '';

  return (
    <div className={`node node-shape node-circle ${selected ? 'selected' : ''} ${overflowClass}`} style={style}>
      {/* Handles kept for edge rendering but disabled for manual connections */}
      <Handle
        type="target"
        position={Position.Top}
        className="connection-handle connection-handle-top"
        isConnectable={false}
      />
      <Handle
        type="target"
        position={Position.Left}
        id="left"
        className="connection-handle connection-handle-left"
        isConnectable={false}
      />

      <div
        className="shape-content nodrag"
        contentEditable
        suppressContentEditableWarning
        onBlur={handleTextChange}
        onKeyDown={handleKeyDown}
        data-placeholder="Circle"
      >
        {nodeData.text}
      </div>

      <Handle
        type="source"
        position={Position.Right}
        id="right"
        className="connection-handle connection-handle-right"
        isConnectable={false}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        className="connection-handle connection-handle-bottom"
        isConnectable={false}
      />

      {/* Add connected node buttons */}
      <button
        className="node-add-btn left"
        onClick={handleAddNode('left')}
        title="Add node to the left"
      >
        +
      </button>

      <button
        className="node-add-btn right"
        onClick={handleAddNode('right')}
        title="Add node to the right"
      >
        +
      </button>

      <button
        className="node-add-btn bottom"
        onClick={handleAddNode('bottom')}
        title="Add node below"
      >
        +
      </button>

      <ResizeHandle nodeId={id} />
    </div>
  );
});

CircleNode.displayName = 'CircleNode';
